import { useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useScroll } from "framer-motion";
import { Move3d } from "lucide-react";
import { MaterialScene, MATERIALS } from "./MaterialScene";
import { Eyebrow, Lines, EASE } from "./Motion";

const COPY = {
  composite: { d: "Low maintenance with no staining or sealing. Resists fading, splinters and rot.", tags: ["Low maintenance", "Hidden fasteners", "Many colors"] },
  hardwood: { d: "Dense, rich and extremely hard-wearing. Takes an oil finish beautifully.", tags: ["Premium look", "Very durable", "Oil finish"] },
  cedar: { d: "Natural warmth, beautiful grain and that cedar smell. Easier on the budget.", tags: ["Natural wood", "Budget friendly", "Stain or seal"] },
  pvc: { d: "The lightest, most moisture-resistant option. Stays cool and clean.", tags: ["Moisture proof", "Cool underfoot", "Easy to clean"] },
};

const useDrag = () => {
  const dragRef = useRef(0);
  const last = useRef(null);
  return {
    dragRef,
    handlers: {
      onPointerDown: (e) => { last.current = e.clientX; e.currentTarget.setPointerCapture(e.pointerId); },
      onPointerMove: (e) => { if (last.current === null) return; dragRef.current += (e.clientX - last.current) * 0.01; last.current = e.clientX; },
      onPointerUp: () => { last.current = null; },
      onPointerCancel: () => { last.current = null; },
    },
  };
};

export const Materials = () => {
  const [kind, setKind] = useState(0);
  const ref = useRef(null);
  const frame = useRef(null);
  const inView = useInView(frame, { once: true, margin: "200px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const { dragRef, handlers } = useDrag();
  const m = MATERIALS[kind];

  return (
    <section id="materials" ref={ref} data-testid="section-materials" className="relative overflow-hidden bg-[#0A0A0A] px-5 py-28 md:px-10 md:py-40">
      <div className="mx-auto grid max-w-[1500px] items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Eyebrow>Pick your decking</Eyebrow>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[1] tracking-tighter text-white md:text-6xl">
            <Lines lines={["Every board,", <span key="m" className="font-serif font-normal italic text-neutral-400">hand-picked.</span>]} />
          </h2>
          <p className="mt-6 max-w-md text-neutral-400">Tap a material to flip the boards. Drag them around. We'll help you choose the right one for your yard, your budget and how you live outside.</p>
          <div className="mt-10 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {MATERIALS.map((x, i) => (
              <button key={x.id} data-testid={`material-option-${x.id}`} onClick={() => setKind(i)}
                className={`relative rounded-full border px-4 py-3 text-sm font-medium transition-colors ${kind === i ? "border-white text-[#0A0A0A]" : "border-white/15 text-neutral-300 hover:border-white/50"}`}>
                {kind === i && <motion.span layoutId="mat-pill" className="absolute inset-0 rounded-full bg-white" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
                <span className="relative">{x.name}</span>
              </button>
            ))}
          </div>
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.div key={m.id} data-testid="material-spec" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45, ease: EASE }}
              className="mt-8 border-t border-white/[0.08] pt-6">
              <div className="font-display text-3xl font-bold tracking-tight text-white">{m.name}</div>
              <p className="mt-3 max-w-md text-neutral-400">{COPY[m.id].d}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {COPY[m.id].tags.map((t) => <span key={t} className="rounded-full border border-white/10 px-3 py-1.5 font-mono text-[0.62rem] uppercase tracking-[0.15em] text-neutral-300">{t}</span>)}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="lg:col-span-7">
          <div ref={frame} {...handlers} data-cursor="Drag" className="corner-ticks relative aspect-[4/3] cursor-grab touch-pan-y overflow-hidden border border-white/[0.08] bg-[radial-gradient(ellipse_at_50%_40%,#1E1E1E_0%,#0A0A0A_70%)]">
            {inView && <MaterialScene kind={kind} dragRef={dragRef} progress={scrollYProgress} testId="material-3d-canvas" className="absolute inset-0" />}
            <div className="pointer-events-none absolute left-5 top-5 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-500">Sample / {m.name}</div>
            <div className="pointer-events-none absolute right-5 top-5 flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-neutral-400"><Move3d className="h-3.5 w-3.5" /> Drag to turn</div>
          </div>
        </div>
      </div>
    </section>
  );
};
